import { createTheme, responsiveFontSizes, Theme, PaletteMode, Components } from '@mui/material/styles';

// Per-tier theme variant. Drives the accent colour and the dark-mode surfaces so
// each dashboard is visually distinct:
//   employee → light / blue   company → dark / blue   owner → dark / red
export type ThemeVariant = 'employee' | 'company' | 'owner';

interface VariantTokens {
  primary: { main: string; light: string; lighter: string; dark: string; contrastText: string };
  darkBgDefault: string;
  darkBgPaper: string;
  darkSurface: string; // AppBar / Drawer background in dark mode
  darkBorder: string;
}

const getVariantTokens = (variant: ThemeVariant): VariantTokens => {
  if (variant === 'owner') {
    // App Owner — dark red command-deck look.
    return {
      primary: { main: '#dc2626', light: '#f87171', lighter: 'rgba(220, 38, 38, 0.10)', dark: '#991b1b', contrastText: '#ffffff' },
      darkBgDefault: '#1a0606',
      darkBgPaper: '#2a0d0d',
      darkSurface: '#2a0d0d',
      darkBorder: '#4c1d1d',
    };
  }
  // Employee + Company share the blue accent; mode (light vs dark) sets them apart.
  return {
    primary: { main: '#2563eb', light: '#60a5fa', lighter: 'rgba(25, 118, 210, 0.08)', dark: '#1d4ed8', contrastText: '#ffffff' },
    darkBgDefault: '#030712',
    darkBgPaper: '#0f172a',
    darkSurface: '#0f172a',
    darkBorder: '#1e293b',
  };
};

// Create base components configuration to be shared between light and dark themes
const getBaseComponents = (mode: PaletteMode, tokens: VariantTokens): any => ({
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: 8,
        padding: '8px 16px',
        '&.MuiButton-contained': {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          },
        },
        '@media (max-width:600px)': {
          padding: '6px 12px',
          fontSize: '0.875rem',
        },
      },
      sizeSmall: {
        padding: '6px 12px',
        '@media (max-width:600px)': {
          padding: '4px 10px',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        borderRadius: 12,
        '@media (max-width:600px)': {
          borderRadius: 8,
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        borderRadius: 12,
        '@media (max-width:600px)': {
          borderRadius: 8,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        padding: 24,
        '@media (max-width:600px)': {
          borderRadius: 8,
          padding: 16,
          margin: 8,
          maxWidth: 'calc(100% - 16px)',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
          '@media (max-width:600px)': {
            fontSize: '0.95rem',
          },
        },
        '@media (max-width:600px)': {
          '& .MuiInputLabel-root': {
            fontSize: '0.95rem',
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        height: 24,
        '& .MuiChip-label': {
          px: 1,
          fontSize: '0.75rem',
        },
        '@media (max-width:600px)': {
          height: 22,
          '& .MuiChip-label': {
            fontSize: '0.7rem',
          },
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: 8,
        '&:hover': {
          backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
        },
        '@media (max-width:600px)': {
          padding: 6,
        },
      },
      sizeSmall: {
        padding: 6,
        '@media (max-width:600px)': {
          padding: 4,
        },
      },
    },
  },
  // Add AppBar component with dark mode styling
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: mode === 'light' ? '#ffffff' : tokens.darkSurface,
        color: mode === 'light' ? '#1e293b' : '#f1f5f9',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        borderBottom: `1px solid ${mode === 'light' ? '#e2e8f0' : tokens.darkBorder}`,
      },
    },
  },
  // Add Drawer component with dark mode styling
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: mode === 'light' ? '#ffffff' : tokens.darkSurface,
        borderRight: `1px solid ${mode === 'light' ? '#e2e8f0' : tokens.darkBorder}`,
        '@media (max-width:600px)': {
          width: '85%',
        },
      },
    },
  },
  // Add ListItemButton with dark mode hover effects
  MuiListItemButton: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
        },
      },
    },
  },
});

// Create the theme based on the mode and per-tier variant
export const getTheme = (mode: PaletteMode = 'dark', variant: ThemeVariant = 'company'): Theme => {
  const isLight = mode === 'light';
  const tokens = getVariantTokens(variant);

  const palette = {
    mode,
    primary: tokens.primary,
    secondary: {
      main: '#7c3aed', // Purple
      light: '#a78bfa',
      dark: '#5b21b6',
      contrastText: '#ffffff',
    },
    background: {
      default: isLight ? '#f8fafc' : tokens.darkBgDefault,
      paper: isLight ? '#ffffff' : tokens.darkBgPaper,
    },
    text: {
      primary: isLight ? '#1e293b' : '#f1f5f9',
      secondary: isLight ? '#64748b' : '#94a3b8',
    },
    error: {
      main: '#dc2626',
      light: '#ef4444',
      dark: '#b91c1c',
    },
    success: {
      main: '#059669',
      light: '#10b981',
      dark: '#047857',
    },
    warning: {
      main: '#d97706',
      light: '#f59e0b',
      dark: '#b45309',
    },
    info: {
      main: '#0891b2',
      light: '#06b6d4',
      dark: '#0e7490',
    },
    divider: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.08)',
  };

  const baseTheme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    palette,
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        letterSpacing: '-0.025em',
        fontSize: '2.5rem',
        '@media (max-width:600px)': {
          fontSize: '2rem',
        },
      },
      h2: {
        fontWeight: 700,
        letterSpacing: '-0.025em',
        fontSize: '2rem',
        '@media (max-width:600px)': {
          fontSize: '1.75rem',
        },
      },
      h3: {
        fontWeight: 600,
        letterSpacing: '-0.025em',
        fontSize: '1.75rem',
        '@media (max-width:600px)': {
          fontSize: '1.5rem',
        },
      },
      h4: {
        fontWeight: 600,
        letterSpacing: '-0.025em',
        fontSize: '1.5rem',
        '@media (max-width:600px)': {
          fontSize: '1.25rem',
        },
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.025em',
        fontSize: '1.25rem',
        '@media (max-width:600px)': {
          fontSize: '1.1rem',
        },
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.025em',
        fontSize: '1rem',
      },
      subtitle1: {
        fontWeight: 500,
      },
      subtitle2: {
        fontWeight: 500,
      },
      button: {
        fontWeight: 500,
        textTransform: 'none',
      },
      body1: {
        fontSize: '1rem',
        '@media (max-width:600px)': {
          fontSize: '0.95rem',
        },
      },
      body2: {
        fontSize: '0.875rem',
        '@media (max-width:600px)': {
          fontSize: '0.85rem',
        },
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: getBaseComponents(mode, tokens),
  });

  return responsiveFontSizes(baseTheme);
};

// Default theme export
const theme = getTheme('light');
export default theme; 